import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useSettings } from "../lib/settings";
import { ChevronDown, ArrowLeft, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard, { ProductSkeleton } from "../components/ProductCard";

function CategorySection({ title, subtitle, products, viewAllUrl, viewAllLabel, loading }) {
  return (
    <section className="pg-section">
      <div className="pg-container">
        <div className="flex items-end justify-between mb-8 md:mb-12 border-b border-[#3A3D43] pb-4">
          <div>
            <div className="text-[#EF1B17] text-xs font-bold uppercase tracking-[0.2em] mb-2">— {subtitle}</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#F4F4F2]">{title}</h2>
          </div>
          <Link to={viewAllUrl} data-testid={`view-all-${viewAllUrl.replace("/", "")}`}
            className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#F4F4F2] hover:text-[#EF1B17] transition-colors">
            {viewAllLabel} <ArrowLeft size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)
            : products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
        <div className="sm:hidden mt-6 text-center">
          <Link to={viewAllUrl} className="inline-flex items-center gap-2 border border-[#3A3D43] rounded-md px-6 py-3 font-bold hover:border-[#EF1B17]">
            {viewAllLabel} <ArrowLeft size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function AgencyMarquee({ agencies }) {
  const nav = useNavigate();
  // Defensive dedupe by canonical id — never render the same agency twice.
  const unique = React.useMemo(() => {
    const seen = new Set();
    return (agencies || []).filter((a) => {
      if (!a?.id || seen.has(a.id)) return false;
      seen.add(a.id); return true;
    });
  }, [agencies]);

  // Only duplicate the visual track for the seamless infinite animation
  // when there are enough unique agencies to actually overflow the viewport.
  // Below this threshold, render once and center — no animation, no adjacent duplicates.
  const SEAMLESS_MIN = 8;
  const shouldAnimate = unique.length >= SEAMLESS_MIN;
  const list = shouldAnimate ? [...unique, ...unique] : unique;

  const Card = ({ a, cloned }) => (
    <button
      key={cloned ? `clone-${a.id}` : a.id}
      data-testid={cloned ? `agency-clone-${a.id}` : `agency-${a.id}`}
      aria-hidden={cloned ? "true" : undefined}
      tabIndex={cloned ? -1 : 0}
      onClick={() => !cloned && nav(`/supplements?agency=${a.id}`)}
      className="group shrink-0 flex flex-col items-center gap-2"
      aria-label={a.name}
    >
      <div className="w-24 h-16 md:w-32 md:h-20 rounded-md border border-[#3A3D43] bg-[#24262B] flex items-center justify-center overflow-hidden grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 group-hover:border-[#EF1B17] transition-all">
        <img src={fileUrl(a.logo_url)} alt={a.name} loading="lazy" className="max-h-12 md:max-h-14 object-contain" width="120" height="60" />
      </div>
      <span className="text-xs text-gray-500 group-hover:text-[#EF1B17] font-bold">{a.name}</span>
    </button>
  );

  return (
    <section aria-label="agencies" className="border-y border-[#3A3D43] bg-[#1e1f23] py-8">
      <div className={"pg-marquee overflow-hidden" + (shouldAnimate ? "" : " flex justify-center")}>
        <div className={"flex items-center gap-12 px-4 " + (shouldAnimate ? "pg-marquee-track" : "")}
             data-testid="agency-track"
             data-animated={shouldAnimate ? "1" : "0"}>
          {shouldAnimate
            ? list.map((a, i) => <Card key={i < unique.length ? a.id : `clone-${a.id}`} a={a} cloned={i >= unique.length} />)
            : list.map((a) => <Card key={a.id} a={a} cloned={false} />)}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings } = useSettings();
  const [agencies, setAgencies] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [supp, setSupp] = useState([]);
  const [cloth, setCloth] = useState([]);
  const [acc, setAcc] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, fp, ps, pc, pa] = await Promise.all([
          api.get("/agencies"),
          api.get("/products/featured"),
          api.get("/products", { params: { type: "supplement", home: true, limit: 8 } }),
          api.get("/products", { params: { type: "clothing", home: true, limit: 8 } }),
          api.get("/products", { params: { type: "accessory", home: true, limit: 8 } }),
        ]);
        setAgencies(a.data);
        setFeatured(fp.data);
        setSupp(ps.data.items);
        setCloth(pc.data.items);
        setAcc(pa.data.items);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="App">
      <Header transparentOverHero />

      {/* Hero */}
      <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center overflow-hidden">
        <img
          src={fileUrl(settings.hero_image) || "https://images.pexels.com/photos/6389516/pexels-photo-6389516.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
          alt="Power Gym Hero" fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#191A1D]/95 via-[#191A1D]/70 to-[#191A1D]/40" />
        <div className="absolute inset-0 pg-grid-lines opacity-40" />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#EF1B17]/20 blur-3xl" />

        <div className="pg-container relative z-10 py-24 md:py-32">
          <div className="max-w-2xl pg-fade-up">
            <div className="inline-flex items-center gap-2 text-[#EF1B17] text-xs font-bold uppercase tracking-[0.3em] mb-4">
              <span className="w-8 h-px bg-[#EF1B17]" /> متجر رياضي كامل
            </div>
            <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-7xl font-black text-[#F4F4F2] leading-[1.05] mb-6">
              {settings.hero_title || "قوتك تبدأ من اختيارك"}
            </h1>
            <p data-testid="hero-desc" className="text-base sm:text-lg text-gray-300 leading-relaxed mb-8 max-w-xl">
              {settings.hero_description || "كل ما تحتاجه من المكملات الغذائية والملابس والإكسسوارات الرياضية، من وكالات موثوقة وفي مكان واحد."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={settings.cta_primary_link || "/supplements"} data-testid="hero-cta-supp"
                className="inline-flex items-center gap-2 bg-[#EF1B17] hover:bg-[#FF3B30] text-white font-bold px-7 py-3.5 rounded-md pg-lift pg-red-glow-soft">
                {settings.cta_primary_label || "تسوّق المكملات"}
              </Link>
              <Link to={settings.cta_secondary_link || "/sportswear"} data-testid="hero-cta-cloth"
                className="inline-flex items-center gap-2 bg-transparent border border-[#3A3D43] hover:border-[#F4F4F2] text-[#F4F4F2] font-bold px-7 py-3.5 rounded-md transition-colors">
                {settings.cta_secondary_label || "تسوّق الملابس"}
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 flex flex-col items-center gap-1 text-xs">
          <span>تصفح</span>
          <ChevronDown className="animate-bounce" size={20} />
        </div>
      </section>

      {/* Agencies */}
      {agencies.length > 0 && <AgencyMarquee agencies={agencies} />}

      {/* Featured spotlight */}
      {featured.length > 0 && (
        <section data-testid="featured-row" className="relative pg-section overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#EF1B17]/10 blur-3xl pointer-events-none" />
          <div className="pg-container relative">
            <div className="flex items-end justify-between mb-8 md:mb-10 border-b border-[#3A3D43] pb-4">
              <div>
                <div className="text-[#EF1B17] text-xs font-bold uppercase tracking-[0.2em] mb-2 inline-flex items-center gap-1">
                  <Sparkles size={12} /> اختيار المتجر
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#F4F4F2]">منتجات مميّزة</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {featured.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {featured.length > 0 && <div className="border-t border-[#3A3D43]/50" />}

      {/* Categories */}
      {settings.show_home_supplements !== false && (
        <>
          <CategorySection
            title="المكملات الغذائية"
            subtitle="جودة عالمية"
            products={supp}
            viewAllUrl="/supplements"
            viewAllLabel="عرض جميع المكملات"
            loading={loading}
          />
          <div className="border-t border-[#3A3D43]/50" />
        </>
      )}

      {settings.show_home_sportswear !== false && (
        <>
          <CategorySection
            title="الملابس الرياضية"
            subtitle="أداء وأناقة"
            products={cloth}
            viewAllUrl="/sportswear"
            viewAllLabel="عرض جميع الملابس"
            loading={loading}
          />
          <div className="border-t border-[#3A3D43]/50" />
        </>
      )}

      {settings.show_home_accessories !== false && (
        <CategorySection
          title="الإكسسوارات الرياضية"
          subtitle="أدوات مساندة"
          products={acc}
          viewAllUrl="/accessories"
          viewAllLabel="عرض جميع الإكسسوارات"
          loading={loading}
        />
      )}

      <Footer />
    </div>
  );
}
